import { useEffect, useState, useRef } from 'react';
import { useIntl } from 'react-intl';
import {
  Container,
  Toast,
  error,
  GetMessageWithIntl,
  success,
  MessageProps,
} from '~/shared/components';
import { mtechnavi } from '~/shared/libs/clientsdk';

import './OcpPrInput.css';
import { useAuth } from '~/shared/contexts/AuthProvider';
import { ViewId, getExceptionMessage } from '~/shared/utils';
import {
  GuidancePanelItem,
  GuidancePanelFooterItem,
} from './parts/GuidanceItem';
import {
  ConfirmationPanelItem,
  ConfirmationPanelFooterItem,
} from './parts/ConfirmationItem';
import {
  AiAssistEditPanelItem,
  AiAssistEditPanelFooterItem,
} from './parts/AiAssistEditItem';
import { EditPanelItem, EditPanelFooterItem } from './parts/EditItem';
import {
  PublicConfirmationPanelItem,
  PublicConfirmationPanelFooterItem,
} from './parts/PublicConfirmationItem';
import {
  listBusinessUnitIntroductions,
  listGeneratedBusinessUnitIntroductionHistorys,
} from '~/tenant/core/pages/OcpPrInput/utils/api';
import Long from 'long';
import { useLoading } from '~/shared/contexts/LoadingProvider';

import {
  ConfirmationDialog,
  EditModeSelectDialog,
} from '~/shared/components/ui';
import {
  PreviewGenerateHistoryDialog,
  PreviewGenerateHistoryItem,
} from '~/shared/components/ui/Dialog/PreviewGenerateHistoryDialog';

const VIEW_ID: ViewId = 'OCP_PR_INPUT';

export function OcpPrInput() {
  // ログイン者の情報特定用email
  const auth = useAuth();
  // 自社企業情報
  const myCompanyInfo = useRef<mtechnavi.api.company.ICompany>();
  const intl = useIntl();
  const { showLoading, hideLoading } = useLoading();
  const toastSuccess = GetMessageWithIntl(intl, { id: 'I0000001' });

  // メッセージ
  const publicConfimarionMessage: MessageProps = {
    id: 'publicConfimarionMessage',
    prefixId: VIEW_ID,
  };
  const tempEditConfimarionMessage: MessageProps = {
    id: 'tempEditConfimarionMessage',
    prefixId: VIEW_ID,
  };
  const discardConfirmationMessage: MessageProps = {
    id: 'discardConfirmationMessage',
    prefixId: VIEW_ID,
  };
  const generatedLimitMessage: MessageProps = {
    id: 'generatedLimitMessage',
    prefixId: VIEW_ID,
  };
  const generateConfirmationMessage: MessageProps = {
    id: 'generateConfimarionMessage',
    prefixId: VIEW_ID,
  };

  const footerRef = useRef<HTMLDivElement>(null);

  // 取得データ格納用 自社企業（紹介情報）マスタ
  const businessUnitIntroductionsData = useRef<
    mtechnavi.api.company.IBusinessUnitIntroduction[]
  >([]);
  // 取得データ格納用 自社企業（紹介情報）生成履歴マスタ
  const generatedBusinessUnitIntroductionHistorysData = useRef<
    mtechnavi.api.company.IGeneratedBusinessUnitIntroductionHistory[]
  >([]);

  // 一時保存データ格納用
  const tempSaveData = useRef<mtechnavi.api.company.IBusinessUnitIntroduction>(
    {}
  );

  // 公開データ格納用
  const publicData = useRef<mtechnavi.api.company.IBusinessUnitIntroduction>(
    {}
  );
  // 初回生成データ格納用
  const initialGeneratedData =
    useRef<mtechnavi.api.company.IGeneratedBusinessUnitIntroductionHistory>({});

  // 生成履歴データ格納用
  const [generateResults, setGenerateResults] = useState<
    PreviewGenerateHistoryItem[]
  >([]);

  const [companyInfo, setCompanyInfo] = useState('');
  const [evaluationIndex1, setEvaluationIndex1] = useState('');
  const [evaluationIndex2, setEvaluationIndex2] = useState('');
  const [evaluationIndex3, setEvaluationIndex3] = useState('');
  const [evaluationIndex4, setEvaluationIndex4] = useState('');
  const [prompt, setPrompt] = useState('');
  const [promptRunCount, setPromptRunCount] = useState(0);

  // 画面制御用
  const [isPublic, setIsPublic] = useState(false);

  // 確認ダイアログ（戻るボタン押下時の破棄確認）
  const [isBackConfirmOpen, setBackConfirmOpen] = useState(false);

  // 確認ダイアログ（公開前確認）
  const [isPublicConfirmOpen, setPublicConfirmOpen] = useState(false);

  // 確認ダイアログ（一時保存データ存在時確認）
  const [isExistTempDataConfirmOpen, setExistTempDataConfirmOpen] =
    useState(false);

  // 確認ダイアログ（生成確認）
  const [isGenerateConfirmOpen, setGenerateConfirmOpen] = useState(false);

  // 編集モード選択ダイアログ
  const [isEditModeSelectOpen, setIsEditModeSelectOpen] = useState(false);

  // 生成履歴ダイアログ
  const [isPreviewGenerateHistoryOpen, setIsPreviewGenerateHistoryOpen] =
    useState(false);

  // 生成上限エラーメッセージダイアログ
  const [isGeneratedLimitMessageOpen, setIsGeneratedLimitMessageOpen] =
    useState(false);

  // タブ制御
  type BreadCrumbName =
    | 'guidance'
    | 'confirmation'
    | 'aiAssistEdit'
    | 'edit'
    | 'publicConfirmation';
  const [breadCrumbs, setBreadCrumbs] = useState<BreadCrumbName[]>([]);
  const [breadCrumbPanel, setBreadCrumbPanel] =
    useState<BreadCrumbName>('guidance');
  const isCurrentBreadCrumb = (target: BreadCrumbName) => {
    return breadCrumbPanel === target;
  };

  // パネル切替時は先頭へスクロール（
  useEffect(() => {
    const id = requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      const main = document.querySelector('.container .main');
      if (main instanceof HTMLElement) {
        main.scrollTop = 0;
      }
    });
    return () => cancelAnimationFrame(id);
  }, [breadCrumbPanel]);

  // 初回画面用データ
  useEffect(() => {
    // viewIDに紐付くpresetを取得
    (async () => {
      showLoading();
      try {
        const [getCompanyRes] = await Promise.all([
          window.App.services.ui.getMyCompany(),
        ]);

        // setPreset(presetItem);
        myCompanyInfo.current = getCompanyRes;

        setBreadCrumbs([
          'guidance',
          'confirmation',
          'aiAssistEdit',
          'edit',
          'publicConfirmation',
        ]);
        setBreadCrumbPanel('guidance');

        // 対象データ取得
        (async () => {
          await reload();
        })();
      } catch (err) {
        error(getExceptionMessage(intl, err));
        throw err;
      } finally {
        hideLoading();
      }
    })();

    // 初回だけ起動させたい処理なのでlintから除外させる
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // [データ取得] 全データ取得
  const reload = async () => {
    // 以下のデータ取得は取引先管理コンテンツの取得後の前提
    try {
      const [
        listBusinessUnitIntroductionsRes,
        listGeneratedBusinessUnitIntroductionHistorysRes,
      ] = await Promise.all([
        listBusinessUnitIntroductions(),
        listGeneratedBusinessUnitIntroductionHistorys(),
      ]);

      // 取得データ格納用 自社企業（紹介情報）マスタ
      businessUnitIntroductionsData.current =
        listBusinessUnitIntroductionsRes.items ?? [];

      // 履歴データの取得
      generatedBusinessUnitIntroductionHistorysData.current =
        listGeneratedBusinessUnitIntroductionHistorysRes.items ?? [];

      // 紹介情報.publishedがfalseの場合、一時保存データとしてセットする
      for (const item of businessUnitIntroductionsData.current) {
        if (!item.published) {
          tempSaveData.current = item;
          setExistTempDataConfirmOpen(true);
        } else {
          publicData.current = item;
          setIsPublic(true);
        }
      }

      if (generatedBusinessUnitIntroductionHistorysData.current.length > 0) {
        // 取得した生成履歴データの生成日付が一番古いデータを初回データとしてセットする
        const sortedResults =
          generatedBusinessUnitIntroductionHistorysData.current.sort((a, b) => {
            return (
              Long.fromValue(a.generatedAt!).toNumber() -
              Long.fromValue(b.generatedAt!).toNumber()
            );
          });
        initialGeneratedData.current = sortedResults[0] ?? {};

        setGenerateResults(
          sortedResults.map((v) => ({
            updateDate: v.generatedAt ?? Long.fromNumber(0),
            prompt: v.prompt ?? '',
            companyOverview: v.businessUnitIntroduction?.companyOverview ?? '',
            evaluationIndex1:
              v.businessUnitIntroduction?.evaluationIndex1 ?? '',
            evaluationIndex2:
              v.businessUnitIntroduction?.evaluationIndex2 ?? '',
            evaluationIndex3:
              v.businessUnitIntroduction?.evaluationIndex3 ?? '',
            evaluationIndex4:
              v.businessUnitIntroduction?.evaluationIndex4 ?? '',
          }))
        );
      }
      // 取得した生成履歴データのうち、本日日付のデータをカウントしてセットする
      const todayGeneratedData =
        generatedBusinessUnitIntroductionHistorysData.current.filter((v) => {
          // UNIXマイクロ秒時間をDate型に変換して比較する
          return (
            new Date(
              Long.fromValue(v.generatedAt!).toNumber() / 1000
            ).toDateString() === new Date().toDateString()
          );
        }).length;
      setPromptRunCount(todayGeneratedData);
    } catch (err) {
      error(getExceptionMessage(intl, err));
      throw err;
    }
  };

  // リセットする
  const reset = () => {
    setCompanyInfo('');
    setEvaluationIndex1('');
    setEvaluationIndex2('');
    setEvaluationIndex3('');
    setEvaluationIndex4('');
    setPrompt('');
    setPromptRunCount(0);
    setIsPublic(false);
    tempSaveData.current = {};
    publicData.current = {};
    initialGeneratedData.current = {};
    generatedBusinessUnitIntroductionHistorysData.current = [];
    setGenerateResults([]);
    businessUnitIntroductionsData.current = [];
  };

  // 初回生成データをセットする
  const setInitialGeneratedData = async (): Promise<void> => {
    // 初回生成データが存在しない場合、生成する
    if (generatedBusinessUnitIntroductionHistorysData.current.length === 0) {
      await handleGenerate('');
    }

    setCompanyInfo(
      initialGeneratedData.current?.businessUnitIntroduction?.companyOverview ??
        ''
    );
    setEvaluationIndex1(
      initialGeneratedData.current?.businessUnitIntroduction
        ?.evaluationIndex1 ?? ''
    );
    setEvaluationIndex2(
      initialGeneratedData.current?.businessUnitIntroduction
        ?.evaluationIndex2 ?? ''
    );
    setEvaluationIndex3(
      initialGeneratedData.current?.businessUnitIntroduction
        ?.evaluationIndex3 ?? ''
    );
    setEvaluationIndex4(
      initialGeneratedData.current?.businessUnitIntroduction
        ?.evaluationIndex4 ?? ''
    );
  };

  // 一時保存状態のデータをセットする
  const setTempSaveData = () => {
    setCompanyInfo(tempSaveData.current.companyOverview ?? '');
    setEvaluationIndex1(tempSaveData.current.evaluationIndex1 ?? '');
    setEvaluationIndex2(tempSaveData.current.evaluationIndex2 ?? '');
    setEvaluationIndex3(tempSaveData.current.evaluationIndex3 ?? '');
    setEvaluationIndex4(tempSaveData.current.evaluationIndex4 ?? '');
  };

  // 公開情報をセットする
  const setPublicData = () => {
    setCompanyInfo(publicData.current.companyOverview ?? '');
    setEvaluationIndex1(publicData.current.evaluationIndex1 ?? '');
    setEvaluationIndex2(publicData.current.evaluationIndex2 ?? '');
    setEvaluationIndex3(publicData.current.evaluationIndex3 ?? '');
    setEvaluationIndex4(publicData.current.evaluationIndex4 ?? '');
  };

  // 共通.戻るボタン押下時の処理
  const handleBack = (v: BreadCrumbName) => {
    setBreadCrumbPanel(v);
  };

  // 共通.確認ページに遷移
  const handleConfirmation = async (): Promise<void> => {
    // 公開情報が存在する場合、公開情報をセットする
    if (isPublic) {
      setPublicData();
    } else {
      // 初回生成データをセットする
      await setInitialGeneratedData();
    }
    setBreadCrumbPanel('confirmation');
  };

  // 一時保存状態のデータをセットして編集ページに遷移
  const handleToEditByTempSave = () => {
    setTempSaveData();
    setBreadCrumbPanel('edit');
  };

  // 確認.編集へ進むボタン押下時の処理
  const handleToEdit = () => {
    // 公開情報を表示している場合
    if (isPublic) {
      setIsEditModeSelectOpen(true);
      return;
    }

    // 初回生成データをセットする
    setInitialGeneratedData();
    setBreadCrumbPanel('aiAssistEdit');
    return;
  };

  // AIアシスト編集.一時保存状態して次へボタン押下時の処理
  const handleTempSave = async () => {
    showLoading();
    try {
      await saveBusinessUnitIntroduction();
    } catch (err) {
      error(getExceptionMessage(intl, err));
      throw err;
    } finally {
      hideLoading();
    }

    setBreadCrumbPanel('edit');
  };

  // 編集.一時保存状態して次へボタン押下時の処理
  const handleTempSaveByEdit = async () => {
    showLoading();
    try {
      await saveBusinessUnitIntroduction();
    } catch (err) {
      error(getExceptionMessage(intl, err));
      throw err;
    } finally {
      hideLoading();
    }
    setBreadCrumbPanel('publicConfirmation');
  };

  // 公開.公開ボタン押下時の処理
  const handlePublic = async () => {
    showLoading();
    try {
      await publishBusinessUnitIntroduction();
      success([toastSuccess]);
      reset();
      await reload();
      setBreadCrumbPanel('guidance');
    } catch (err) {
      error(getExceptionMessage(intl, err));
      throw err;
    } finally {
      hideLoading();
    }
  };

  // AIアシスト編集.生成ボタン押下時の処理
  const handleGenerate = async (prompt: string) => {
    showLoading();
    try {
      const request: mtechnavi.api.company.IGenerateBusinessUnitIntroductionRequest =
        {
          prompt: prompt,
        };
      const [generateRes] = await window.App.services.ui.worker.apiCall({
        actionName: 'generateBusinessUnitIntroduction',
        request: request,
      });

      if (generateRes.generatedBusinessUnitIntroduction) {
        // 生成したデータを画面にセットする
        setCompanyInfo(
          generateRes.generatedBusinessUnitIntroduction.companyOverview ?? ''
        );
        setEvaluationIndex1(
          generateRes.generatedBusinessUnitIntroduction.evaluationIndex1 ?? ''
        );
        setEvaluationIndex2(
          generateRes.generatedBusinessUnitIntroduction.evaluationIndex2 ?? ''
        );
        setEvaluationIndex3(
          generateRes.generatedBusinessUnitIntroduction.evaluationIndex3 ?? ''
        );
        setEvaluationIndex4(
          generateRes.generatedBusinessUnitIntroduction.evaluationIndex4 ?? ''
        );

        // 初回だけ生成履歴データをセット
        if (
          generatedBusinessUnitIntroductionHistorysData.current.length === 0
        ) {
          await refreshGenerateHistory();
        } else {
          setPromptRunCount(promptRunCount + 1);
        }
      }
    } catch (err) {
      error(getExceptionMessage(intl, err));
      throw err;
    } finally {
      hideLoading();
    }
  };

  // 生成履歴を取得する
  const refreshGenerateHistory = async () => {
    try {
      const [listGeneratedBusinessUnitIntroductionHistorysRes] =
        await Promise.all([listGeneratedBusinessUnitIntroductionHistorys()]);

      generatedBusinessUnitIntroductionHistorysData.current =
        listGeneratedBusinessUnitIntroductionHistorysRes.items ?? [];

      if (generatedBusinessUnitIntroductionHistorysData.current.length > 0) {
        const sortedResults =
          generatedBusinessUnitIntroductionHistorysData.current.sort((a, b) => {
            return (
              Long.fromValue(a.generatedAt!).toNumber() -
              Long.fromValue(b.generatedAt!).toNumber()
            );
          });
        // 取得した生成履歴データの生成日付が一番古いデータを初回データとしてセットする
        initialGeneratedData.current = sortedResults[0] ?? {};
        // 取得した生成履歴データのうち、本日日付のデータをカウントしてセットする
        const todayGeneratedData =
          generatedBusinessUnitIntroductionHistorysData.current.filter((v) => {
            // UNIXマイクロ秒時間をDate型に変換して比較する
            return (
              new Date(
                Long.fromValue(v.generatedAt!).toNumber() / 1000
              ).toDateString() === new Date().toDateString()
            );
          }).length;
        setPromptRunCount(todayGeneratedData);

        setGenerateResults(
          sortedResults.map((v) => ({
            updateDate: v.generatedAt ?? Long.fromNumber(0),
            prompt: v.prompt ?? '',
            companyOverview: v.businessUnitIntroduction?.companyOverview ?? '',
            evaluationIndex1:
              v.businessUnitIntroduction?.evaluationIndex1 ?? '',
            evaluationIndex2:
              v.businessUnitIntroduction?.evaluationIndex2 ?? '',
            evaluationIndex3:
              v.businessUnitIntroduction?.evaluationIndex3 ?? '',
            evaluationIndex4:
              v.businessUnitIntroduction?.evaluationIndex4 ?? '',
          }))
        );
      }
    } catch (err) {
      error(getExceptionMessage(intl, err));
      throw err;
    }
  };

  // 生成履歴.ダイアログを表示
  const handlePreviewGenerateHistory = async () => {
    showLoading();
    await refreshGenerateHistory();
    hideLoading();
    setIsPreviewGenerateHistoryOpen(true);
  };

  // 生成履歴.紹介情報を画面にセット
  const handleSetIntroductionByGenerateHistory = (
    item: PreviewGenerateHistoryItem
  ) => {
    setCompanyInfo(item.companyOverview ?? '');
    setEvaluationIndex1(item.evaluationIndex1 ?? '');
    setEvaluationIndex2(item.evaluationIndex2 ?? '');
    setEvaluationIndex3(item.evaluationIndex3 ?? '');
    setEvaluationIndex4(item.evaluationIndex4 ?? '');
    setPrompt(item.prompt ?? '');
  };

  // [API]一時保存処理
  const saveBusinessUnitIntroduction = async () => {
    // businessUnitIntroductionId と updatedAt は一時保存データからセットする
    const request: mtechnavi.api.company.IBusinessUnitIntroduction = {
      companyId: myCompanyInfo.current?.companyId,
      businessUnitIntroductionId:
        tempSaveData.current.businessUnitIntroductionId ?? '',
      companyOverview: companyInfo,
      evaluationIndex1: evaluationIndex1,
      evaluationIndex2: evaluationIndex2,
      evaluationIndex3: evaluationIndex3,
      evaluationIndex4: evaluationIndex4,
      updatedAt: tempSaveData.current.updatedAt ?? Long.fromNumber(0),
    };
    const saveRes = await window.App.services.ui.worker.apiCall({
      actionName: 'saveOcpIntroduction',
      request: request,
    });
    tempSaveData.current = saveRes[0];
  };

  // [API]公開処理
  const publishBusinessUnitIntroduction = async () => {
    // businessUnitIntroductionId と updatedAt は一時保存データからセットする
    const request: mtechnavi.api.company.IBusinessUnitIntroduction =
      tempSaveData.current;
    const publishRes = await window.App.services.ui.worker.apiCall({
      actionName: 'publishOcpIntroduction',
      request: request,
    });
    setIsPublic(true);
  };

  return (
    <>
      <Container viewId={VIEW_ID}>
        <div className="OcpPrInput">
          <div className="info">
            <div className="basic-info-body">
              <div className="bread-crumb-panel-contents">
                {breadCrumbs.map((v, index) => {
                  const selectPane = v === breadCrumbPanel ? 'current' : '';
                  return (
                    <>
                      <div
                        key={`breadCrumb-${index}`}
                        className={`bread-crumb-panel ${selectPane}`}
                      >
                        {GetMessageWithIntl(intl, { prefixId: VIEW_ID, id: v })}
                      </div>
                      {index < breadCrumbs.length - 1 && (
                        <div className="bread-crumb-list"> ＞ </div>
                      )}
                    </>
                  );
                })}
              </div>
              {isCurrentBreadCrumb('guidance') && <GuidancePanelItem type="" />}
              {isCurrentBreadCrumb('confirmation') && (
                <ConfirmationPanelItem
                  companyInfo={companyInfo ?? ''}
                  evaluationIndex1={evaluationIndex1 ?? ''}
                  evaluationIndex2={evaluationIndex2 ?? ''}
                  evaluationIndex3={evaluationIndex3 ?? ''}
                  evaluationIndex4={evaluationIndex4 ?? ''}
                  isPublic={isPublic}
                />
              )}
              {isCurrentBreadCrumb('aiAssistEdit') && (
                <AiAssistEditPanelItem
                  onGenerate={() => {
                    if (promptRunCount >= 20) {
                      setIsGeneratedLimitMessageOpen(true);
                      return;
                    }
                    setGenerateConfirmOpen(true);
                  }}
                  onPreviewGenerateHistory={() => {
                    handlePreviewGenerateHistory();
                  }}
                  companyInfo={companyInfo ?? ''}
                  evaluationIndex1={evaluationIndex1 ?? ''}
                  evaluationIndex2={evaluationIndex2 ?? ''}
                  evaluationIndex3={evaluationIndex3 ?? ''}
                  evaluationIndex4={evaluationIndex4 ?? ''}
                  prompt={prompt ?? ''}
                  promptRunCount={promptRunCount ?? 0}
                  onPromptChange={setPrompt}
                />
              )}
              {isCurrentBreadCrumb('edit') && (
                <EditPanelItem
                  companyInfo={companyInfo ?? ''}
                  evaluationIndex1={evaluationIndex1 ?? ''}
                  evaluationIndex2={evaluationIndex2 ?? ''}
                  evaluationIndex3={evaluationIndex3 ?? ''}
                  evaluationIndex4={evaluationIndex4 ?? ''}
                  onCompanyInfoChange={setCompanyInfo}
                  onEvaluationIndex1Change={setEvaluationIndex1}
                  onEvaluationIndex2Change={setEvaluationIndex2}
                  onEvaluationIndex3Change={setEvaluationIndex3}
                  onEvaluationIndex4Change={setEvaluationIndex4}
                />
              )}
              {isCurrentBreadCrumb('publicConfirmation') && (
                <PublicConfirmationPanelItem
                  companyInfo={companyInfo ?? ''}
                  evaluationIndex1={evaluationIndex1 ?? ''}
                  evaluationIndex2={evaluationIndex2 ?? ''}
                  evaluationIndex3={evaluationIndex3 ?? ''}
                  evaluationIndex4={evaluationIndex4 ?? ''}
                />
              )}
            </div>
          </div>
        </div>
        <div className="footer" ref={footerRef}>
          <div className="footer-contents">
            <div className="input-line">
              {isCurrentBreadCrumb('guidance') && (
                <GuidancePanelFooterItem
                  onToConfirm={() => {
                    handleConfirmation();
                  }}
                />
              )}
              {isCurrentBreadCrumb('confirmation') && (
                <ConfirmationPanelFooterItem
                  onBack={() => {
                    handleBack('guidance');
                  }}
                  onToEdit={() => {
                    handleToEdit();
                  }}
                />
              )}
              {isCurrentBreadCrumb('aiAssistEdit') && (
                <AiAssistEditPanelFooterItem
                  onBack={() => {
                    handleConfirmation();
                  }}
                  onTempSave={() => {
                    handleTempSave();
                  }}
                />
              )}
              {isCurrentBreadCrumb('edit') && (
                <EditPanelFooterItem
                  onBack={() => {
                    setBackConfirmOpen(true);
                  }}
                  onTempSave={() => {
                    handleTempSaveByEdit();
                  }}
                />
              )}
              {isCurrentBreadCrumb('publicConfirmation') && (
                <PublicConfirmationPanelFooterItem
                  onBack={() => {
                    handleBack('edit');
                  }}
                  onPublic={() => {
                    setPublicConfirmOpen(true);
                  }}
                />
              )}
            </div>
          </div>
        </div>
        <Toast />
      </Container>
      {/* 一時保存データ存在時確認ダイアログ */}
      <ConfirmationDialog
        isOpen={isExistTempDataConfirmOpen}
        viewMessage={tempEditConfimarionMessage}
        decisionLabel={GetMessageWithIntl(intl, { id: 'yes' })}
        cancelLabel={GetMessageWithIntl(intl, { id: 'no' })}
        onDecision={() => {
          handleToEditByTempSave();
          setExistTempDataConfirmOpen(false);
        }}
        onCancel={() => {
          setExistTempDataConfirmOpen(false);
        }}
      />
      {/* 生成上限エラーメッセージダイアログ */}
      <ConfirmationDialog
        isOpen={isGeneratedLimitMessageOpen}
        viewMessage={generatedLimitMessage}
        onCancel={() => {
          setIsGeneratedLimitMessageOpen(false);
        }}
      />
      {/* 生成確認ダイアログ */}
      <ConfirmationDialog
        isOpen={isGenerateConfirmOpen}
        viewMessage={generateConfirmationMessage}
        onDecision={() => {
          handleGenerate(prompt ?? '');
          setGenerateConfirmOpen(false);
        }}
        onCancel={() => {
          setGenerateConfirmOpen(false);
        }}
      />
      {/* 破棄確認ダイアログ */}
      <ConfirmationDialog
        isOpen={isBackConfirmOpen}
        viewMessage={discardConfirmationMessage}
        onDecision={() => {
          handleConfirmation();
          setBackConfirmOpen(false);
        }}
        onCancel={() => {
          setBackConfirmOpen(false);
        }}
      />
      {/* 編集モード選択ダイアログ */}
      <EditModeSelectDialog
        isOpen={isEditModeSelectOpen}
        onAIEditDecision={() => {
          setInitialGeneratedData();
          setBreadCrumbPanel('aiAssistEdit');
          setIsEditModeSelectOpen(false);
        }}
        onStandardEditDecision={() => {
          setPublicData();
          setBreadCrumbPanel('edit');
          setIsEditModeSelectOpen(false);
        }}
        onCancel={() => {
          setIsEditModeSelectOpen(false);
        }}
      />
      {/* 生成履歴ダイアログ */}
      <PreviewGenerateHistoryDialog
        isOpen={isPreviewGenerateHistoryOpen}
        inputOption={{
          isPublic: isPublic,
          generateResults: generateResults,
          publishedInfo: {
            updateDate: publicData.current.updatedAt,
            prompt: '',
            companyOverview: publicData.current.companyOverview ?? '',
            evaluationIndex1: publicData.current.evaluationIndex1 ?? '',
            evaluationIndex2: publicData.current.evaluationIndex2 ?? '',
            evaluationIndex3: publicData.current.evaluationIndex3 ?? '',
            evaluationIndex4: publicData.current.evaluationIndex4 ?? '',
          },
          defaultSample: (() => {
            return {
              updateDate:
                initialGeneratedData.current?.generatedAt != null
                  ? Long.fromValue(initialGeneratedData.current?.generatedAt)
                  : Long.fromNumber(0),
              prompt: initialGeneratedData.current?.prompt ?? '',
              companyOverview:
                initialGeneratedData.current?.businessUnitIntroduction
                  ?.companyOverview ?? '',
              evaluationIndex1:
                initialGeneratedData.current?.businessUnitIntroduction
                  ?.evaluationIndex1 ?? '',
              evaluationIndex2:
                initialGeneratedData.current?.businessUnitIntroduction
                  ?.evaluationIndex2 ?? '',
              evaluationIndex3:
                initialGeneratedData.current?.businessUnitIntroduction
                  ?.evaluationIndex3 ?? '',
              evaluationIndex4:
                initialGeneratedData.current?.businessUnitIntroduction
                  ?.evaluationIndex4 ?? '',
            };
          })(),
          tempSave: {
            updateDate: tempSaveData.current.updatedAt,
            prompt: '',
            companyOverview: tempSaveData.current.companyOverview ?? '',
            evaluationIndex1: tempSaveData.current.evaluationIndex1 ?? '',
            evaluationIndex2: tempSaveData.current.evaluationIndex2 ?? '',
            evaluationIndex3: tempSaveData.current.evaluationIndex3 ?? '',
            evaluationIndex4: tempSaveData.current.evaluationIndex4 ?? '',
          },
        }}
        onDecision={(item: PreviewGenerateHistoryItem) => {
          handleSetIntroductionByGenerateHistory(item);
          setIsPreviewGenerateHistoryOpen(false);
        }}
        onCancel={() => {
          setIsPreviewGenerateHistoryOpen(false);
        }}
      />
      {/* 公開前確認ダイアログ */}
      <ConfirmationDialog
        isOpen={isPublicConfirmOpen}
        viewMessage={publicConfimarionMessage}
        onDecision={() => {
          handlePublic();
          setPublicConfirmOpen(false);
        }}
        onCancel={() => {
          setPublicConfirmOpen(false);
        }}
      />
    </>
  );
}
